import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

import { RootState } from '../../../store';
import { getLongPullUIState } from '../../../store/UI/longPull.slice';
import { SkeletonLoader } from '../../UI';

interface Props {
  dialogState: FolderManagerSlidesDialogState;
  onDismiss: (data: FolderManagerSlidesDialogState | null, shouldRefetch?: boolean) => void;
}

interface Slide {
  slide_number: number;
  thumbnail_base64: string;
  slide_title?: string | null;
}

interface ApiResponse {
  data?: Slide[];
  status?: string;
  Status?: string;
}

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(245px, 1fr));
  gap: 20px;
  padding: 20px;
  width: 100%;
`;

const SlideItem = styled.div`
  width: 245px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const SlideThumbnail = styled.div`
  width: 245px;
  height: 138px;
  border-radius: ${({ theme }) => theme.radius.default};
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.grey || '#f5f5f5'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const SlideTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.default};
  color: ${({ theme }) => theme.palette.text?.secondary || '#555'};
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SlideNumber = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
`;

export const FolderManagerSlidesDialog: FC<Props> = ({ dialogState, onDismiss }) => {
  const uploadItems = useSelector((state: RootState) => {
    const longPullState = getLongPullUIState(state);
    return longPullState?.['global'] || [];
  });
  const uploadStatus = uploadItems.find((item: any) => item.id === dialogState.jobId);

  const apiResponseData: ApiResponse | null = useMemo(() => {
    if (!uploadStatus?.responseData) return null;

    try {
      const data =
        typeof uploadStatus.responseData === 'string'
          ? JSON.parse(uploadStatus.responseData)
          : uploadStatus.responseData;

      // handle new format: { Status: 'success', Slides: [...] }
      if (data?.Slides) {
        return { data: data.Slides, status: data.Status };
      }

      // handle legacy format where data is the array directly
      return { data: Array.isArray(data) ? data : [] };
    } catch (error) {
      console.error('Failed to parse API response:', error);
      return null;
    }
  }, [uploadStatus?.responseData]);

  const slides = apiResponseData?.data || [];
  const apiStatus = uploadStatus?.apiStatus?.toLowerCase?.() || '';
  const isCompleted = uploadStatus?.isCompleted || ['success', 'failed', 'timeout'].includes(apiStatus);

  const handleDismiss = () => {
    onDismiss(dialogState, isCompleted);
  };

  return (
    <Dialog fullScreen open={dialogState.opened} onClose={handleDismiss} sx={{ zIndex: 99999 }}>
      <AppBar sx={{ position: 'relative', background: 'white' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleDismiss} aria-label="close">
            <CloseIcon style={{ color: 'black' }} />
          </IconButton>
          <Typography variant="h6" sx={{ marginLeft: 2, flex: 1 }} style={{ color: 'black' }}>
            Processing {dialogState.title} {slides.length > 0 && `(${slides.length})`}
          </Typography>
        </Toolbar>
      </AppBar>

      <div style={{ padding: 20, overflowY: 'auto' }}>
        {!uploadStatus && <p>Initializing upload...</p>}

        {uploadStatus && (
          <>
            <SlidesGrid>
              {slides.length > 0 ? (
                slides.map((slide) => (
                  <SlideItem key={slide.slide_number}>
                    <SlideThumbnail>
                      {slide.thumbnail_base64 ? (
                        <>
                          <SlideImage
                            src={`data:image/png;base64,${slide.thumbnail_base64}`}
                            alt={`Slide ${slide.slide_number}`}
                          />
                          <SlideNumber>{slide.slide_number}</SlideNumber>
                        </>
                      ) : (
                        <SkeletonLoader count={1} height={138} width={245} single />
                      )}
                    </SlideThumbnail>
                    {slide.slide_title && <SlideTitle>{slide.slide_title}</SlideTitle>}
                  </SlideItem>
                ))
              ) : (
                <p>Waiting for slides data...</p>
              )}
            </SlidesGrid>
          </>
        )}
      </div>
    </Dialog>
  );
};
